import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { toast } from 'sonner';
import { ArrowLeft, BarChart3, GitBranch, Trophy, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PlaceNode from '../components/nodes/PlaceNode';
import TransitionNode from '../components/nodes/TransitionNode';
import MetricsDashboard from '../components/MetricsDashboard';
import ClusteredPetriNetView from '../components/ClusteredPetriNetView';
import PetriNetPreviewModal from '../components/PetriNetPreviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const nodeTypes = {
  place: PlaceNode,
  transition: TransitionNode,
};

// ReactFlow pro options moved outside component to prevent re-renders
const reactFlowProOptions = { hideAttribution: true };

const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', ranksep: 150, nodesep: 80 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: node.type === 'place' ? 80 : 150, height: node.type === 'place' ? 80 : 60 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.position = {
      x: nodeWithPosition.x - (node.type === 'place' ? 40 : 75),
      y: nodeWithPosition.y - (node.type === 'place' ? 40 : 30),
    };
  });

  return { nodes, edges };
};

// Helper: pick badge class for a conformance value (0..1) based on quality thresholds
const getConformanceBadgeClass = (value) => {
  if (value >= 0.8) return 'bg-emerald-50 border-emerald-300 text-emerald-700';
  if (value >= 0.5) return 'bg-amber-50 border-amber-300 text-amber-700';
  return 'bg-red-50 border-red-300 text-red-700';
};

// Helper: pick grid column class based on number of objectives
const getNsgaGridColsClass = (count) => {
  if (count >= 3) return 'md:grid-cols-3';
  if (count === 2) return 'md:grid-cols-2';
  return 'md:grid-cols-1';
};

const ProcessViewer = () => {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMetrics, setShowMetrics] = useState(false);
  const [showClusterPanel, setShowClusterPanel] = useState(false);
  const [numClusters, setNumClusters] = useState(3);
  const [clusterAlgorithm, setClusterAlgorithm] = useState('kmeans');
  const [clusterParams, setClusterParams] = useState({
    pegawai_terbanyak: true,
    pegawai_tersedikit: false,
    biaya_termahal: true,
    biaya_termurah: false,
    waktu_terlama: true,
    waktu_terpendek: false,
  });

  // Mutually-exclusive toggle within a group: checking one unchecks the other; unchecking remains allowed
  const toggleExclusiveParam = useCallback((selfKey, otherKey) => {
    setClusterParams(prev => {
      const willCheck = !prev[selfKey];
      return {
        ...prev,
        [selfKey]: willCheck,
        [otherKey]: willCheck ? false : prev[otherKey],
      };
    });
  }, []);
  const [clustering, setClustering] = useState(false);
  const [clusterResults, setClusterResults] = useState(null);
  const [showOriginal, setShowOriginal] = useState(true);
  const [previewCluster, setPreviewCluster] = useState(null);
  const [algorithmUsed, setAlgorithmUsed] = useState(null);
  const [conformance, setConformance] = useState(null);

  // Hide ReactFlow attribution aggressively with CSS injection
  useEffect(() => {
    // Inject additional CSS to cover attribution using textContent (XSS-safe)
    const style = document.createElement('style');
    style.textContent = `
      .react-flow__panel.bottom,
      .react-flow__attribution,
      a[href*="reactflow"],
      a[href*="emergent"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
      }
      
      /* White overlay at bottom right */
      .react-flow__renderer {
        position: relative;
      }
      
      .react-flow__renderer::after {
        content: '';
        position: absolute;
        bottom: 0;
        right: 0;
        width: 220px;
        height: 50px;
        background: #f9fafb;
        z-index: 9999;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
    
    const hideAttribution = () => {
      // Try multiple selectors
      const selectors = [
        '.react-flow__attribution',
        'a[href*="reactflow"]',
        '[class*="attribution"]',
        '.react-flow__panel.react-flow__attribution',
        'div[style*="position"][style*="bottom"][style*="right"]'
      ];
      
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el && el.textContent && (el.textContent.includes('Made with') || el.textContent.includes('Emergent'))) {
            el.remove();
          } else if (el) {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
            el.style.opacity = '0';
            el.style.pointerEvents = 'none';
          }
        });
      });
      
      // Also check for any small divs at bottom right
      const allDivs = document.querySelectorAll('div');
      allDivs.forEach(div => {
        const style = window.getComputedStyle(div);
        const position = style.position;
        const bottom = style.bottom;
        const right = style.right;
        
        if (div.textContent && (div.textContent.includes('Made with') || div.textContent.includes('Emergent'))) {
          div.remove();
        }
      });
    };
    
    hideAttribution();
    
    // Run periodically
    const interval = setInterval(hideAttribution, 200);
    
    // Also observe DOM changes
    const observer = new MutationObserver(hideAttribution);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearInterval(interval);
      observer.disconnect();
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [petriNetResponse, metricsResponse] = await Promise.all([
          axios.get(`${API}/petri-net/${resultId}`),
          axios.get(`${API}/metrics/${resultId}`),
        ]);

        const petriNet = petriNetResponse.data;
        const metricsData = metricsResponse.data;
        
        // Set algorithm used if available
        if (petriNet.algorithm) {
          setAlgorithmUsed(petriNet.algorithm);
        }
        // Set conformance metrics if available
        if (petriNet.conformance) {
          setConformance(petriNet.conformance);
        }

        const flowNodes = [
          ...petriNet.places.map((place) => ({
            id: place.id,
            type: 'place',
            data: { 
              label: place.label, 
              isStart: place.isStart, 
              isEnd: place.isEnd,
              tokens: place.tokens
            },
            position: { x: 0, y: 0 },
          })),
          ...petriNet.transitions.map((transition) => ({
            id: transition.id,
            type: 'transition',
            data: { 
              label: transition.label,
              isSilent: transition.isSilent
            },
            position: { x: 0, y: 0 },
          })),
        ];

        const flowEdges = petriNet.arcs.map((arc) => ({
          id: arc.id,
          source: arc.source,
          target: arc.target,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#71717A', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#71717A',
          },
        }));

        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
          flowNodes,
          flowEdges
        );

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
        setMetrics(metricsData);
        setLoading(false);
        
        // Hide attribution after render
        setTimeout(() => {
          const attribution = document.querySelector('.react-flow__attribution');
          if (attribution) {
            attribution.style.display = 'none';
          }
        }, 100);
      } catch (error) {
        toast.error('Failed to load process mining results');
        setLoading(false);
      }
    };

    if (resultId) {
      fetchData();
    }
  }, [resultId, setNodes, setEdges]);

  const handleExportPNG = useCallback(() => {
    toast.info('PNG export feature coming soon!');
  }, []);

  const handleExportJSON = useCallback(() => {
    const data = {
      nodes: nodes,
      edges: edges,
      metrics: metrics,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `process-mining-${resultId}.json`;
    link.href = url;
    link.click();
    toast.success('Data exported as JSON');
  }, [nodes, edges, metrics, resultId]);

  const handleClustering = useCallback(async () => {
    const selectedParams = Object.keys(clusterParams).filter(key => clusterParams[key]);
    
    if (selectedParams.length === 0) {
      toast.error('Pilih minimal 1 parameter untuk klasterisasi');
      return;
    }
    
    setClustering(true);
    try {
      const paramsQuery = selectedParams.join(',');
      const response = await axios.post(`${API}/cluster/${resultId}?algorithm=${clusterAlgorithm}&params=${paramsQuery}`);
      const clusteringData = response.data;
      
      // Attach selected params keys for frontend filtering of metrics display
      setClusterResults({ ...clusteringData, selected_param_keys: selectedParams });
      setShowClusterPanel(false); // Close panel after success
      toast.success(`Proses berhasil diklaster menjadi ${clusteringData.clusters.length} kelompok menggunakan ${clusteringData.algorithm}`);
      setClustering(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal melakukan klasterisasi');
      setClustering(false);
    }
  }, [clusterParams, clusterAlgorithm, resultId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-lime-200 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400" style={{ fontFamily: 'Manrope, sans-serif' }}>Loading Petri Net...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-gradient-to-r from-white via-gray-50 to-white border-b-2 border-gray-300 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 text-zinc-700 hover:text-zinc-900 transition-all border-2 border-gray-300"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-zinc-800" style={{ fontFamily: 'Syne, sans-serif' }}>
                Visualisasi <span className="bg-gradient-to-r from-emerald-600 to-lime-600 bg-clip-text text-transparent">Petri Net</span>
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm">
                {algorithmUsed && (
                  <p className="text-zinc-500" data-testid="algorithm-used">
                    Algoritma: <span className="font-semibold text-emerald-600">{algorithmUsed}</span>
                  </p>
                )}
                {conformance && (conformance.fitness !== null || conformance.precision !== null) && (
                  <div className="flex items-center gap-2" data-testid="conformance-badges">
                    {conformance.fitness !== null && (() => {
                      const v = conformance.fitness;
                      const cls = getConformanceBadgeClass(v);
                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-medium ${cls}`}
                          title="Token-based replay fitness: seberapa baik model memutar trace event log. 1.0 = sempurna."
                          data-testid="fitness-badge"
                        >
                          Fitness: <span className="font-bold">{(v * 100).toFixed(1)}%</span>
                        </span>
                      );
                    })()}
                    {conformance.precision !== null && (() => {
                      const v = conformance.precision;
                      const cls = getConformanceBadgeClass(v);
                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-medium ${cls}`}
                          title="ETC-based precision: seberapa ketat model membatasi behavior. 1.0 = tidak ada underfitting."
                          data-testid="precision-badge"
                        >
                          Precision: <span className="font-bold">{(v * 100).toFixed(1)}%</span>
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowClusterPanel(!showClusterPanel)}
              className="bg-gradient-to-r from-emerald-100 to-lime-100 border-2 border-emerald-300 text-emerald-700 hover:from-emerald-200 hover:to-lime-200 transition-all rounded-full px-5 py-2.5 font-medium text-sm shadow-md"
              data-testid="toggle-cluster-button"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              Klasterisasi Proses
            </Button>
            <Button
              onClick={() => setShowMetrics(!showMetrics)}
              className="bg-white border-2 border-gray-300 text-zinc-700 hover:bg-gray-50 transition-all rounded-full px-5 py-2.5 font-medium text-sm shadow-md"
              data-testid="toggle-metrics-button"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {showMetrics ? 'Sembunyikan' : 'Tampilkan'} Metrics
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        <div className={`flex-1 bg-gray-50 relative ${showMetrics ? 'w-2/3' : 'w-full'}`}>
          {/* Clustering Panel */}
          {showClusterPanel && (
            <div className="absolute top-3 left-3 z-10 bg-white backdrop-blur-xl rounded-xl shadow-2xl p-4 border-2 border-emerald-200 w-96">
              <h3 className="text-base font-bold text-zinc-800 mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                Klasterisasi Proses
              </h3>
              <p className="text-xs text-zinc-600 mb-3">
                Kelompokkan model Petri Net berdasarkan parameter yang dipilih
              </p>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-700 mb-1">
                    Algoritma Klasterisasi
                  </label>
                  <select
                    value={clusterAlgorithm}
                    onChange={(e) => setClusterAlgorithm(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border-2 border-zinc-300 rounded-md focus:border-emerald-500 focus:outline-none text-zinc-800 bg-white cursor-pointer"
                    data-testid="algorithm-select"
                  >
                    <option value="kmeans">K-Means Clustering</option>
                    <option value="markov">Markov Cluster (MCL)</option>
                  </select>
                  <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">
                    {clusterAlgorithm === 'kmeans' 
                      ? 'Algoritma berbasis centroid untuk pengelompokan data' 
                      : 'Algoritma berbasis graf untuk deteksi komunitas'}
                  </p>
                </div>
                
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <label className="block text-xs font-medium text-zinc-700">
                      Parameter Klasterisasi
                    </label>
                    <p className="text-[11px] text-zinc-500">Pilih min. 1 parameter</p>
                  </div>
                  
                  <div className="space-y-1.5">
                    {/* Pegawai Group - mutually exclusive */}
                    <div className="rounded-md border border-gray-200 px-2.5 py-1.5">
                      <p className="text-[11px] font-semibold text-zinc-700 mb-1">Pegawai <span className="text-zinc-400 font-normal">(pilih salah satu)</span></p>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={clusterParams.pegawai_terbanyak}
                            onChange={() => toggleExclusiveParam('pegawai_terbanyak', 'pegawai_tersedikit')}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                            data-testid="param-pegawai-terbanyak"
                          />
                          <span className="text-zinc-700">Terbanyak</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={clusterParams.pegawai_tersedikit}
                            onChange={() => toggleExclusiveParam('pegawai_tersedikit', 'pegawai_terbanyak')}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                            data-testid="param-pegawai-tersedikit"
                          />
                          <span className="text-zinc-700">Tersedikit</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Biaya Group - mutually exclusive */}
                    <div className="rounded-md border border-gray-200 px-2.5 py-1.5">
                      <p className="text-[11px] font-semibold text-zinc-700 mb-1">Biaya <span className="text-zinc-400 font-normal">(pilih salah satu)</span></p>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={clusterParams.biaya_termahal}
                            onChange={() => toggleExclusiveParam('biaya_termahal', 'biaya_termurah')}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                            data-testid="param-biaya-termahal"
                          />
                          <span className="text-zinc-700">Termahal</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={clusterParams.biaya_termurah}
                            onChange={() => toggleExclusiveParam('biaya_termurah', 'biaya_termahal')}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                            data-testid="param-biaya-termurah"
                          />
                          <span className="text-zinc-700">Termurah</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Waktu Group - mutually exclusive */}
                    <div className="rounded-md border border-gray-200 px-2.5 py-1.5">
                      <p className="text-[11px] font-semibold text-zinc-700 mb-1">Waktu <span className="text-zinc-400 font-normal">(pilih salah satu)</span></p>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={clusterParams.waktu_terlama}
                            onChange={() => toggleExclusiveParam('waktu_terlama', 'waktu_terpendek')}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                            data-testid="param-waktu-terlama"
                          />
                          <span className="text-zinc-700">Terlama</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={clusterParams.waktu_terpendek}
                            onChange={() => toggleExclusiveParam('waktu_terpendek', 'waktu_terlama')}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                            data-testid="param-waktu-terpendek"
                          />
                          <span className="text-zinc-700">Terpendek</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={handleClustering}
                    disabled={clustering || Object.values(clusterParams).every(v => !v)}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-lime-500 text-white text-sm font-bold hover:from-emerald-600 hover:to-lime-600 transition-all rounded-md px-3 py-2 shadow-md disabled:opacity-50"
                    data-testid="cluster-button"
                  >
                    {clustering ? 'Memproses...' : 'Mulai Klasterisasi'}
                  </Button>
                  <Button
                    onClick={() => setShowClusterPanel(false)}
                    className="px-3 py-2 text-sm bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-all rounded-md border border-zinc-300"
                  >
                    Tutup
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            proOptions={reactFlowProOptions}
            className="bg-white"
            zoomOnScroll={true}
            panOnDrag={true}
            minZoom={0.1}
            maxZoom={4}
          >
            <Background color="#e5e7eb" gap={64} style={{ backgroundColor: '#f9fafb' }} />
            <Controls className="bg-white border-2 border-zinc-300 rounded-lg shadow-lg" />
            <MiniMap 
              nodeColor={(node) => {
                if (node.type === 'place') return '#10b981';
                if (node.type === 'transition') return '#84cc16';
                return '#71717A';
              }}
              maskColor="rgba(255, 255, 255, 0.8)"
              className="bg-white border-2 border-zinc-300"
            />
          </ReactFlow>
        </div>

        {showMetrics && metrics && (
          <div className="w-1/3 bg-black/50 backdrop-blur-xl border-l border-zinc-800 overflow-y-auto">
            <MetricsDashboard metrics={metrics} />
          </div>
        )}
      </div>

      {/* Clustered Results Section */}
      {clusterResults && (
        <div className="bg-gradient-to-b from-gray-50 to-white p-8 border-t border-gray-300">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-zinc-800 mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                Hasil <span className="bg-gradient-to-r from-emerald-600 to-lime-600 bg-clip-text text-transparent">Klasterisasi</span>
              </h2>
              <p className="text-zinc-600">
                Proses bisnis diklaster menjadi <span className="font-bold text-emerald-700">{clusterResults.clusters.length}</span> kelompok menggunakan algoritma <span className="font-semibold text-emerald-600">{clusterResults.algorithm || 'K-Means'}</span>
              </p>
              {clusterResults.parameters_used && clusterResults.parameters_used.length > 0 && (
                <div className="mt-3 flex items-center justify-center gap-2 flex-wrap" data-testid="clustering-params-display">
                  <span className="text-sm text-zinc-600">Parameter klasterisasi:</span>
                  {clusterResults.parameters_used.map((param) => (
                    <span
                      key={`param-${param}`}
                      className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium border border-emerald-300"
                    >
                      {param}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Original Petri Net */}
            <div className="mb-8">
              <ClusteredPetriNetView
                petriNet={clusterResults.original_petri_net}
                title="Model Proses Bisnis Keseluruhan (Original)"
                numCases={clusterResults.original_metrics.total_cases}
                isOriginal={true}
                businessAnalysis={clusterResults.original_business_analysis}
              />
            </div>

            {/* Clustered Petri Nets */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {clusterResults.clusters.map((cluster) => (
                <ClusteredPetriNetView
                  key={cluster.cluster_id}
                  petriNet={cluster.petri_net}
                  title={cluster.cluster_name}
                  numCases={cluster.num_cases}
                  isOriginal={false}
                  onPreview={() => setPreviewCluster(cluster)}
                  businessAnalysis={cluster.business_analysis}
                  selectedParams={clusterResults.selected_param_keys}
                />
              ))}
            </div>

            {/* Optimal Cluster Analysis using NSGA-II */}
            {clusterResults.clusters.length > 1 && (
              <div className="mt-8 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl border-2 border-amber-300 p-4 shadow-lg">
                <div className="flex items-center gap-3 mb-1">
                  <div className="p-2 bg-amber-100 rounded-xl border-2 border-amber-300">
                    <Trophy className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-zinc-800" style={{ fontFamily: 'Syne, sans-serif' }}>
                      Analisis Klaster Optimal
                    </h3>
                    <p className="text-xs text-amber-700 font-medium">
                      Algoritma Multi-Objective Non-dominated Sorting Genetic II (NSGA-II)
                    </p>
                  </div>
                </div>
                <p className="text-xs text-zinc-600 mb-3 ml-12">
                  Optimasi multi-objektif berdasarkan parameter klasterisasi yang dipilih: {(clusterResults.parameters_used || []).join(', ')}
                </p>

                {/* NSGA-II Implementation - parameter-driven */}
                {(() => {
                  const clusters = clusterResults.clusters;
                  const allKeys = ['pegawai_terbanyak', 'pegawai_tersedikit', 'biaya_termahal', 'biaya_termurah', 'waktu_terlama', 'waktu_terpendek'];
                  const objKeys = (clusterResults.selected_param_keys && clusterResults.selected_param_keys.length > 0)
                    ? clusterResults.selected_param_keys
                    : allKeys;

                  const NSGA_META = {
                    pegawai_terbanyak: { label: 'Pegawai Terbanyak', short: 'Pegawai (Max)', color: 'blue', format: 'number', unit: 'pegawai' },
                    pegawai_tersedikit: { label: 'Pegawai Tersedikit', short: 'Pegawai (Min)', color: 'cyan', format: 'number', unit: 'pegawai' },
                    biaya_termahal: { label: 'Biaya Termahal', short: 'Biaya (Max)', color: 'red', format: 'currency', unit: '' },
                    biaya_termurah: { label: 'Biaya Termurah', short: 'Biaya (Min)', color: 'green', format: 'currency', unit: '' },
                    waktu_terlama: { label: 'Waktu Terlama', short: 'Waktu (Max)', color: 'orange', format: 'duration', unit: '' },
                    waktu_terpendek: { label: 'Waktu Terpendek', short: 'Waktu (Min)', color: 'emerald', format: 'duration', unit: '' },
                  };

                  const fmtNum = (v) => (v ?? 0).toLocaleString('id-ID');
                  const fmtCur = (v) => `Rp ${(v ?? 0).toLocaleString('id-ID')}`;
                  const fmtDur = (v) => {
                    const n = v ?? 0;
                    if (n === 0) return '-';
                    if (n >= 1440) {
                      const d = Math.floor(n / 1440);
                      const h = Math.floor((n % 1440) / 60);
                      return `${d.toLocaleString('id-ID')} hari ${h} jam`;
                    }
                    if (n >= 60) {
                      const h = Math.floor(n / 60);
                      const m = Math.round(n % 60);
                      return `${h.toLocaleString('id-ID')} jam ${m} menit`;
                    }
                    return `${Math.round(n).toLocaleString('id-ID')} menit`;
                  };
                  const formatVal = (v, fmt) => {
                    if (fmt === 'currency') return fmtCur(v);
                    if (fmt === 'duration') return fmtDur(v);
                    return fmtNum(v);
                  };

                  const colorBorder = { blue: 'border-blue-200', cyan: 'border-cyan-200', red: 'border-red-200', green: 'border-green-200', orange: 'border-orange-200', emerald: 'border-emerald-200' };
                  const colorBorderActive = { blue: 'border-blue-400 ring-2 ring-blue-200', cyan: 'border-cyan-400 ring-2 ring-cyan-200', red: 'border-red-400 ring-2 ring-red-200', green: 'border-green-400 ring-2 ring-green-200', orange: 'border-orange-400 ring-2 ring-orange-200', emerald: 'border-emerald-400 ring-2 ring-emerald-200' };
                  const colorText = { blue: 'text-blue-700', cyan: 'text-cyan-700', red: 'text-red-700', green: 'text-green-700', orange: 'text-orange-700', emerald: 'text-emerald-700' };
                  const colorChipBg = { blue: 'bg-blue-100 text-blue-700', cyan: 'bg-cyan-100 text-cyan-700', red: 'bg-red-100 text-red-700', green: 'bg-green-100 text-green-700', orange: 'bg-orange-100 text-orange-700', emerald: 'bg-emerald-100 text-emerald-700' };

                  // Build solutions: objectives = values for each selected key (all minimization)
                  const solutions = clusters.map((cluster, idx) => ({
                    index: idx,
                    cluster: cluster,
                    objectives: objKeys.map(k => Number(cluster.business_analysis?.[k] ?? 0))
                  }));

                  // NSGA-II Step 1: Non-dominated Sorting (minimization for all)
                  const dominates = (a, b) => {
                    let strictlyBetter = false;
                    for (let i = 0; i < a.objectives.length; i++) {
                      if (a.objectives[i] > b.objectives[i]) return false;
                      if (a.objectives[i] < b.objectives[i]) strictlyBetter = true;
                    }
                    return strictlyBetter;
                  };

                  const dominationInfo = solutions.map(s => ({
                    ...s,
                    dominationCount: 0,
                    dominatedBy: [],
                    dominates: []
                  }));

                  for (let i = 0; i < dominationInfo.length; i++) {
                    for (let j = 0; j < dominationInfo.length; j++) {
                      if (i !== j) {
                        if (dominates(dominationInfo[i], dominationInfo[j])) {
                          dominationInfo[i].dominates.push(j);
                        }
                        if (dominates(dominationInfo[j], dominationInfo[i])) {
                          dominationInfo[i].dominationCount++;
                          dominationInfo[i].dominatedBy.push(j);
                        }
                      }
                    }
                  }

                  const fronts = [];
                  let currentFront = dominationInfo.filter(s => s.dominationCount === 0);
                  let frontIndex = 0;

                  while (currentFront.length > 0) {
                    fronts.push(currentFront.map(s => ({ ...s, front: frontIndex })));
                    const nextFront = [];
                    for (const solution of currentFront) {
                      for (const dominatedIdx of solution.dominates) {
                        const dominated = dominationInfo[dominatedIdx];
                        dominated.dominationCount--;
                        if (dominated.dominationCount === 0 && !nextFront.includes(dominated)) {
                          nextFront.push(dominated);
                        }
                      }
                    }
                    currentFront = nextFront;
                    frontIndex++;
                  }

                  const paretoFront = fronts[0] || [];

                  const calculateCrowdingDistance = (front) => {
                    if (front.length <= 2) {
                      return front.map(s => ({ ...s, crowdingDistance: Infinity }));
                    }
                    const result = front.map(s => ({ ...s, crowdingDistance: 0 }));
                    const numObjectives = result[0].objectives.length;
                    for (let m = 0; m < numObjectives; m++) {
                      result.sort((a, b) => a.objectives[m] - b.objectives[m]);
                      result[0].crowdingDistance = Infinity;
                      result[result.length - 1].crowdingDistance = Infinity;
                      const fMax = result[result.length - 1].objectives[m];
                      const fMin = result[0].objectives[m];
                      const range = fMax - fMin || 1;
                      for (let i = 1; i < result.length - 1; i++) {
                        result[i].crowdingDistance += (result[i + 1].objectives[m] - result[i - 1].objectives[m]) / range;
                      }
                    }
                    return result;
                  };

                  const paretoWithCrowding = calculateCrowdingDistance(paretoFront);
                  paretoWithCrowding.sort((a, b) => b.crowdingDistance - a.crowdingDistance);
                  const bestSolution = paretoWithCrowding[0];

                  // Compute max value per objective for normalized scoring
                  const objMax = objKeys.map((_, i) => Math.max(1, ...solutions.map(s => s.objectives[i] || 0)));

                  // Rank solutions across all fronts
                  const rankedSolutions = [];
                  fronts.forEach((front, frontIdx) => {
                    const frontWithCrowding = calculateCrowdingDistance(front);
                    frontWithCrowding.sort((a, b) => b.crowdingDistance - a.crowdingDistance);
                    frontWithCrowding.forEach((s) => {
                      rankedSolutions.push({
                        ...s,
                        rank: rankedSolutions.length + 1,
                        frontRank: frontIdx + 1
                      });
                    });
                  });

                  return (
                    <>
                      {/* Pareto Front Info */}
                      <div className="mb-3 bg-white/80 rounded-lg p-3 border border-amber-200">
                        <p className="text-xs text-zinc-700 mb-1.5">
                          <span className="font-bold text-amber-700">Pareto Front (Non-dominated Solutions):</span>{' '}
                          {paretoFront.length} klaster teridentifikasi sebagai solusi non-dominan
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {paretoFront.map((s) => (
                            <span
                              key={`pareto-${s.cluster.cluster_id}`}
                              className="px-2.5 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-medium border border-amber-300"
                            >
                              {s.cluster.cluster_name}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Skor explainer */}
                      <p className="text-[11px] text-zinc-600 mb-2 italic leading-snug px-1">
                        <span className="font-semibold text-amber-700 not-italic">Skor Efisiensi:</span> persentase yang menunjukkan seberapa rendah nilai parameter klaster ini dibandingkan klaster dengan nilai tertinggi (terburuk). Semakin tinggi % efisiensi → klaster lebih hemat/efisien pada parameter tersebut. Skor 100% = nilai 0 (optimal), 0% = klaster dengan nilai paling tinggi.
                      </p>

                      {/* Best Cluster per Objective - dynamic based on selected params */}
                      <div className={`grid grid-cols-1 ${getNsgaGridColsClass(objKeys.length)} gap-3`} data-testid="nsga-best-by-cards">
                        {objKeys.map((key, i) => {
                          const meta = NSGA_META[key];
                          if (!meta) return null;
                          const best = [...solutions].sort((a, b) => a.objectives[i] - b.objectives[i])[0];
                          if (!best) return null;
                          const isParetoOptimal = paretoFront.some(p => p.index === best.index);
                          const efficiency = ((1 - best.objectives[i] / objMax[i]) * 100).toFixed(1);
                          return (
                            <div
                              key={`best-${key}`}
                              className={`bg-white rounded-lg p-3 border-2 shadow-sm ${isParetoOptimal ? colorBorderActive[meta.color] : colorBorder[meta.color]}`}
                            >
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Award className={`w-4 h-4 ${colorText[meta.color]}`} />
                                <span className={`text-xs font-bold ${colorText[meta.color]}`}>Minimum {meta.label}</span>
                                {isParetoOptimal && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${colorChipBg[meta.color]}`}>Pareto</span>}
                              </div>
                              <p className="text-sm font-bold text-zinc-800">{best.cluster.cluster_name}</p>
                              <p className="text-xs text-zinc-600 mt-0.5">
                                {formatVal(best.objectives[i], meta.format)}{meta.unit ? ` ${meta.unit}` : ''}
                              </p>
                              <p className="mt-1.5 text-[11px] text-zinc-500">
                                Skor: <span className="font-semibold">{efficiency}%</span> efisiensi
                              </p>
                            </div>
                          );
                        })}
                      </div>

                      {/* NSGA-II Ranking Table - dynamic columns */}
                      <div className="mt-3 bg-white rounded-lg p-3 border-2 border-amber-200 shadow-sm">
                        <h4 className="text-sm font-bold text-zinc-800 mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                          Peringkat NSGA-II (Non-dominated Sorting + Crowding Distance)
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs" data-testid="nsga-ranking-table">
                            <thead>
                              <tr className="bg-amber-50">
                                <th className="px-2.5 py-2 text-left font-bold text-amber-800 border-b-2 border-amber-200">Peringkat</th>
                                <th className="px-2.5 py-2 text-left font-bold text-amber-800 border-b-2 border-amber-200">Klaster</th>
                                <th className="px-2.5 py-2 text-left font-bold text-amber-800 border-b-2 border-amber-200">Front</th>
                                {objKeys.map((key) => {
                                  const meta = NSGA_META[key];
                                  return (
                                    <th key={`th-${key}`} className="px-2.5 py-2 text-right font-bold text-amber-800 border-b-2 border-amber-200">
                                      {meta?.short || key}
                                    </th>
                                  );
                                })}
                                <th className="px-2.5 py-2 text-right font-bold text-amber-800 border-b-2 border-amber-200">Crowding Distance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rankedSolutions.map((s) => {
                                const getRowClass = () => {
                                  if (s.rank === 1) return 'bg-emerald-50';
                                  if (s.rank % 2 === 0) return 'bg-gray-50';
                                  return 'bg-white';
                                };
                                return (
                                  <tr key={`rank-${s.cluster.cluster_id}`} className={getRowClass()}>
                                    <td className="px-2.5 py-1.5 border-b border-gray-200">
                                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs ${s.rank === 1 ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-700'}`}>
                                        {s.rank}
                                      </span>
                                    </td>
                                    <td className="px-2.5 py-1.5 border-b border-gray-200 font-medium text-zinc-800">{s.cluster.cluster_name}</td>
                                    <td className="px-2.5 py-1.5 border-b border-gray-200">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.frontRank === 1 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                                        Front {s.frontRank}
                                      </span>
                                    </td>
                                    {objKeys.map((key, i) => {
                                      const meta = NSGA_META[key];
                                      return (
                                        <td key={`td-${s.cluster.cluster_id}-${key}`} className="px-2.5 py-1.5 border-b border-gray-200 text-right text-zinc-600">
                                          {formatVal(s.objectives[i], meta?.format || 'number')}
                                        </td>
                                      );
                                    })}
                                    <td className="px-2.5 py-1.5 border-b border-gray-200 text-right text-zinc-600">
                                      {s.crowdingDistance === Infinity ? '∞' : s.crowdingDistance.toFixed(3)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Overall Best Solution - dynamic param values */}
                      <div className="mt-3 bg-gradient-to-r from-emerald-100 to-lime-100 rounded-lg p-3 border-2 border-emerald-300" data-testid="nsga-best-overall">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-emerald-200 rounded-lg border-2 border-emerald-400">
                            <Trophy className="w-6 h-6 text-emerald-700" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-emerald-700">
                              Klaster Optimal NSGA-II (Pareto Front + Crowding Distance Tertinggi)
                            </p>
                            <p className="text-xl font-bold text-emerald-800 leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                              {bestSolution.cluster.cluster_name}
                            </p>
                            <p className="text-xs text-emerald-700 mt-0.5">
                              {bestSolution.cluster.num_cases} cases • Front {bestSolution.front + 1}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-1 text-[11px] text-emerald-700">
                              {objKeys.map((key, i) => {
                                const meta = NSGA_META[key];
                                if (!meta) return null;
                                return (
                                  <span key={`bestv-${key}`}>
                                    <span className="font-medium">{meta.short || meta.label}:</span>{' '}
                                    <span className="font-semibold">{formatVal(bestSolution.objectives[i], meta.format)}</span>
                                  </span>
                                );
                              })}
                            </div>
                            <p className="text-[10px] text-emerald-600 mt-1">
                              Crowding Distance: {bestSolution.crowdingDistance === Infinity ? '∞ (Solusi boundary)' : bestSolution.crowdingDistance.toFixed(3)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setClusterResults(null)}
                className="px-6 py-3 bg-white hover:bg-gray-50 text-zinc-800 rounded-full transition-all border-2 border-zinc-300 shadow-md font-medium"
              >
                Tutup Hasil Klasterisasi
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Preview Modal */}
      {previewCluster && (
        <PetriNetPreviewModal
          petriNet={previewCluster.petri_net}
          title={previewCluster.cluster_name}
          numCases={previewCluster.num_cases}
          metrics={previewCluster.metrics}
          onClose={() => setPreviewCluster(null)}
        />
      )}
    </div>
  );
};

export default ProcessViewer;